/**
 * Cadastro de itens.
 *
 * DUAS COISAS AQUI NÃO SÃO ÓBVIAS E FORAM DECIDIDAS DE PROPÓSITO:
 *
 * 1. A galeria vai INTEIRA ao salvar, na ordem final — o que não foi enviado,
 *    some. É o mesmo contrato do sistema atual. Parece perigoso, mas é o que
 *    torna remover e reordenar triviais: sem isso, seria preciso inventar uma
 *    linguagem de diferenças ("apague a 2, mova a 3 para o lugar da 1") que
 *    erra mais e explica pior.
 *
 * 2. Excluir e desativar são coisas diferentes, e a tela não deixa confundir.
 *    Item com movimento no histórico NÃO pode ser apagado — o banco recusa, e
 *    é proteção: apagar deixaria o histórico de estoque apontando para o nada.
 *    Quando isso acontece, a tela oferece desativar, que é o que a pessoa
 *    queria de verdade.
 */

import { api, ErroApi, type ItemDaLista } from '../../api.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { escapar, numero } from '../../comum/formato.js';
import { carregarTaxonomia, type Taxonomia } from './taxonomia.js';

interface Foto {
  caminho: string;
  url: string;
}

interface ItemDetalhe {
  id: number;
  codigo: string;
  nome: string;
  /** Outros nomes/grafias erradas, separados por vírgula — só para a busca. */
  sinonimos?: string;
  categoria_id: number | null;
  grupo_id: number | null;
  subgrupo_id: number | null;
  unidade: string | null;
  valor_unitario: string | number | null;
  tipo_controle: string | null;
  tem_validade: boolean;
  controla_unidades: boolean;
  criterio_valor: string | null;
  medida1_valor: string | null;
  medida1_unidade: string | null;
  medida2_valor: string | null;
  medida2_unidade: string | null;
  obs: string | null;
  ativo: boolean;
  fotos: Foto[];
  estoque: { fisico: number; comprometido: number; disponivel: number };
}

const CONTROLES = ['quantidade', 'peso', 'volume', 'unidade'] as const;

/**
 * Como o saldo é contado. Lista fechada (decisão do Lucas, 04/08): campo
 * aberto produzia variação — o banco tem "l" e "L", "pct" que ninguém sabe se
 * era "pacote". Faltando uma, acrescenta-se AQUI, de propósito: uma linha de
 * código é mais barata que limpar unidade suja em 3 mil itens.
 */
const UNIDADES_ESTOQUE = ['un', 'm', 'kg', 'l', 'cx', 'sc', 'par', 'gl', 'pct', 'rolo', 'br', 'm²', 'm³'];

/**
 * Opções de um `<select>`, garantindo que o valor JÁ GRAVADO sempre aparece —
 * mesmo se não estiver na lista oficial. Sem isso, abrir um item antigo com
 * "L" maiúsculo mostraria a primeira opção da lista e SALVAR trocaria a
 * unidade dele em silêncio, só por ter aberto a ficha.
 */
function opcoesDe(lista: string[], atual: string | null | undefined): string {
  const completa = atual && !lista.includes(atual) ? [atual, ...lista] : lista;
  return (
    `<option value="">—</option>` +
    completa
      .map((u) => `<option value="${escapar(u)}" ${u === atual ? 'selected' : ''}>${escapar(u)}</option>`)
      .join('')
  );
}

export function montarCadastroItens(raiz: HTMLElement): void {
  let taxonomia: Taxonomia | undefined;
  let busca = '';
  let mostrar: 'ativos' | 'inativos' | 'todos' = 'ativos';

  raiz.innerHTML = `
    <div class="barra-busca">
      <input id="busca" placeholder="Procure por nome, código ou categoria…" autocomplete="off">
      <select id="mostrar">
        <option value="ativos">Só ativos</option>
        <option value="inativos">Só desativados</option>
        <option value="todos">Todos</option>
      </select>
      <button class="btn" id="novo">+ Novo item</button>
    </div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#lista')!;
  const campoBusca = raiz.querySelector<HTMLInputElement>('#busca')!;

  let temporizador: number | undefined;
  campoBusca.addEventListener('input', () => {
    window.clearTimeout(temporizador);
    temporizador = window.setTimeout(() => {
      busca = campoBusca.value.trim();
      void carregar();
    }, 300);
  });
  raiz.querySelector<HTMLSelectElement>('#mostrar')!.addEventListener('change', (e) => {
    mostrar = (e.target as HTMLSelectElement).value as typeof mostrar;
    void carregar();
  });
  raiz.querySelector('#novo')!.addEventListener('click', () => void abrirFicha(null));

  async function carregar() {
    try {
      if (!taxonomia) taxonomia = await carregarTaxonomia();
      const p = new URLSearchParams({ porPagina: '100', ativo: mostrar });
      if (busca) p.set('busca', busca);
      const r = await api.get<{ itens: (ItemDaLista & { ativo: boolean })[]; total: number; porSemelhanca: boolean }>(
        `/api/itens?${p}`,
      );
      desenhar(r.itens, r.total, r.porSemelhanca);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
    }
  }

  function desenhar(itens: (ItemDaLista & { ativo: boolean })[], total: number, porSemelhanca: boolean) {
    if (itens.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>Nenhum item${busca ? ' para essa busca' : ''}.</p></div>`;
      return;
    }
    lista.innerHTML = `
      ${porSemelhanca ? `<p class="dica">Nada exato para “${escapar(busca)}”; mostrando os parecidos.</p>` : ''}
      <p class="dica">${total} item(ns).</p>
      <div class="rolagem">
        <table class="tabela">
          <thead><tr>
            <th>Código</th><th>Item</th><th>Categoria</th>
            <th class="num">Saldo</th><th>Un.</th><th></th>
          </tr></thead>
          <tbody>${itens
            .map(
              (i) => `<tr class="${i.ativo ? '' : 'linha-inativa'}">
                <td class="cod">${escapar(i.codigo)}</td>
                <td>
                  ${escapar(i.nome)}
                  ${i.ativo ? '' : '<span class="selo-inativo">desativado</span>'}
                  ${i.medida ? `<span class="sub">${escapar(i.medida)}${i.medida2 ? ` · ${escapar(i.medida2)}` : ''}</span>` : ''}
                </td>
                <td>${escapar(i.categoria ?? '—')}</td>
                <td class="num">${numero(i.saldo)}</td>
                <td class="un">${escapar(i.unidade ?? '')}</td>
                <td><button class="btn btn-claro" data-abrir="${escapar(i.codigo)}">Abrir</button></td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>
      </div>`;
    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((b) => {
      b.addEventListener('click', () => void abrirFicha(b.dataset['abrir']!));
    });
  }

  // ------------------------------------------------------------------- ficha

  async function abrirFicha(codigo: string | null) {
    if (!taxonomia) taxonomia = await carregarTaxonomia();
    let item: ItemDetalhe | null = null;
    if (codigo) {
      lista.innerHTML = `<p class="carregando">Abrindo ${escapar(codigo)}…</p>`;
      try {
        item = await api.get<ItemDetalhe>(`/api/itens/${encodeURIComponent(codigo)}`);
      } catch (erro) {
        lista.innerHTML = `<p class="aviso">${escapar(
          erro instanceof ErroApi ? erro.message : 'Não consegui abrir.',
        )}</p>`;
        return;
      }
    }
    // Cópia de trabalho da galeria: só vira definitiva ao salvar.
    const fotos: Foto[] = item ? [...item.fotos] : [];
    desenharFicha(item, fotos);
  }

  function desenharFicha(item: ItemDetalhe | null, fotos: Foto[]) {
    const t = taxonomia!;
    const novo = !item;

    lista.innerHTML = `
      <button class="voltar" id="voltar">← Todos os itens</button>
      <form class="ficha" id="ficha" novalidate>
        <header class="ficha-topo">
          <div>
            <span class="rotulo">${novo ? 'Novo item' : 'Item'}</span>
            <h2>${escapar(item?.nome ?? 'Cadastrar')}</h2>
          </div>
          ${
            item && !item.ativo
              ? `<span class="estado e-cancelado">Desativado</span>`
              : ''
          }
        </header>

        ${
          item
            ? `<dl class="ficha-dados">
                 <div><dt>Físico</dt><dd>${numero(item.estoque.fisico)}</dd></div>
                 <div><dt>Comprometido</dt><dd>${numero(item.estoque.comprometido)}</dd></div>
                 <div><dt>Disponível</dt><dd>${numero(item.estoque.disponivel)}</dd></div>
               </dl>`
            : ''
        }

        <div class="form-grade">
          ${
            novo
              ? `<div class="rotulado estreito">
                   <span>Código</span>
                   <p class="codigo-auto" id="codigo-previa">gerado ao salvar</p>
                   <small class="dica">O sistema cria: prefixo da categoria + próximo número. Ninguém digita — é assim que não há duplicado nem buraco.</small>
                 </div>`
              : `<label class="rotulado estreito">
                   <span>Código</span>
                   <input name="codigo" value="${escapar(item?.codigo ?? '')}" readonly maxlength="40">
                   <small class="dica">O código não muda — é a referência do histórico.</small>
                 </label>`
          }
          <label class="rotulado largo">
            <span>Nome</span>
            <input name="nome" value="${escapar(item?.nome ?? '')}" maxlength="300" required>
          </label>

          <label class="rotulado">
            <span>Categoria</span>
            <select name="categoriaId" id="cat">
              <option value="">—</option>
              ${t.categorias
                .map(
                  (c) =>
                    `<option value="${c.id}" ${c.id === item?.categoria_id ? 'selected' : ''}>${escapar(c.nome)}</option>`,
                )
                .join('')}
            </select>
          </label>
          <label class="rotulado">
            <span>Grupo</span>
            <select name="grupoId" id="gru"></select>
          </label>
          <label class="rotulado">
            <span>Subgrupo</span>
            <select name="subgrupoId" id="sub"></select>
          </label>

          <label class="rotulado estreito">
            <span>Unidade de estoque</span>
            <select name="unidade">${opcoesDe(UNIDADES_ESTOQUE, item?.unidade)}</select>
            <small class="dica">Como o saldo é contado: 123 <b>un</b>, 40 <b>m</b>…</small>
          </label>
          <label class="rotulado estreito">
            <span>Valor unitário</span>
            <input name="valorUnitario" type="number" step="0.01" min="0" value="${
              item?.valor_unitario != null ? Number(item.valor_unitario) : ''
            }">
          </label>
          <label class="rotulado estreito">
            <span>Tipo de controle</span>
            <select name="tipoControle">
              <option value="">—</option>
              ${CONTROLES.map(
                (c) => `<option value="${c}" ${c === item?.tipo_controle ? 'selected' : ''}>${c}</option>`,
              ).join('')}
            </select>
          </label>
          <label class="rotulado estreito caixa">
            <input type="checkbox" name="temValidade" ${item?.tem_validade ? 'checked' : ''}>
            <span>Controla validade</span>
          </label>
          <label class="rotulado estreito caixa" title="Cada exemplar tem patrimônio, série, foto e estado — o pedido vira empréstimo com prazo">
            <input type="checkbox" name="controlaUnidades" ${item?.controla_unidades ? 'checked' : ''}>
            <span>Controla unidades (patrimônio)</span>
          </label>
        </div>

        <h3>Medida</h3>
        <p class="dica">
          Serve para achar “tudo de 32 mm” cruzando categorias — por isso é separada da
          hierarquia, e não um quarto nível dela.
        </p>
        <div class="conf-campos">
          <label class="rotulado estreito">
            <span>Valor</span>
            <input name="medida1Valor" value="${escapar(item?.medida1_valor ?? '')}" maxlength="30">
          </label>
          <label class="rotulado estreito">
            <span>Unidade</span>
            <select name="medida1Unidade">${opcoesDe(t.unidades.map((u) => u.sigla), item?.medida1_unidade)}</select>
          </label>
        </div>
        <details class="medida2" ${item?.medida2_valor ? 'open' : ''}>
          <summary>+ 2ª medida</summary>
          <p class="dica">Para peça com bitola diferente de cada lado, como adaptador de tubo.</p>
          <div class="conf-campos">
            <label class="rotulado estreito">
              <span>Valor</span>
              <input name="medida2Valor" value="${escapar(item?.medida2_valor ?? '')}" maxlength="30">
            </label>
            <label class="rotulado estreito">
              <span>Unidade</span>
              <select name="medida2Unidade">${opcoesDe(t.unidades.map((u) => u.sigla), item?.medida2_unidade)}</select>
            </label>
          </div>
        </details>

        <h3>Fotos</h3>
        <p class="dica">A primeira é a capa — é ela que aparece nas listas e na vitrine.</p>
        <div class="galeria" id="galeria"></div>
        <div class="conf-acoes">
          <label class="btn btn-claro arquivo">
            Adicionar foto
            <input type="file" accept="image/*" id="arquivo" hidden multiple>
          </label>
          <button type="button" class="btn btn-claro" id="buscar-imagens">🔍 Buscar imagens no Google</button>
        </div>
        <span id="subindo" class="dica"></span>
        <div id="sugestoes-imagens"></div>

        <h3>Sinônimos <span class="sub-inline">outros nomes e grafias erradas — ajudam a busca a achar</span></h3>
        <label class="rotulado">
          <input name="sinonimos" maxlength="500" value="${escapar(item?.sinonimos ?? '')}"
                 placeholder="separe por vírgula — ex.: alabanca, labanca, pé de cabra">
          <small class="dica">Só para a busca (vitrine, importar lista, estoque). Não aparece em romaneio nem etiqueta.</small>
        </label>

        <h3>Observações</h3>
        <label class="rotulado">
          <textarea name="obs" rows="3" maxlength="2000">${escapar(item?.obs ?? '')}</textarea>
        </label>

        <div class="conf-acoes">
          <button type="submit" class="btn" id="salvar">${novo ? 'Cadastrar' : 'Salvar'}</button>
          ${
            item
              ? `<button type="button" class="btn btn-claro" id="ativar">${
                  item.ativo ? 'Desativar' : 'Reativar'
                }</button>
                 <button type="button" class="btn btn-perigo" id="excluir">Excluir</button>`
              : ''
          }
        </div>
        <p class="aviso" id="erro" hidden></p>
      </form>`;

    const form = lista.querySelector<HTMLFormElement>('#ficha')!;
    const erro = lista.querySelector<HTMLElement>('#erro')!;

    lista.querySelector('#voltar')!.addEventListener('click', () => void carregar());

    // --- hierarquia dependente ---
    const cat = form.querySelector<HTMLSelectElement>('#cat')!;
    const gru = form.querySelector<HTMLSelectElement>('#gru')!;
    const sub = form.querySelector<HTMLSelectElement>('#sub')!;

    function encherGrupos(selecionado?: number | null) {
      const id = Number(cat.value) || null;
      const opcoes = t.grupos.filter((g) => g.categoria_id === id);
      gru.innerHTML =
        `<option value="">—</option>` +
        opcoes
          .map((g) => `<option value="${g.id}" ${g.id === selecionado ? 'selected' : ''}>${escapar(g.nome)}</option>`)
          .join('');
      gru.disabled = !id;
      encherSubgrupos(gru.value ? Number(gru.value) : null);
    }
    function encherSubgrupos(grupoSelecionado: number | null, selecionado?: number | null) {
      const opcoes = t.subgrupos.filter((s) => s.grupo_id === grupoSelecionado);
      sub.innerHTML =
        `<option value="">—</option>` +
        opcoes
          .map((s) => `<option value="${s.id}" ${s.id === selecionado ? 'selected' : ''}>${escapar(s.nome)}</option>`)
          .join('');
      sub.disabled = !grupoSelecionado;
    }
    // Ao abrir, respeita o que já estava gravado; ao trocar, limpa o nível de baixo
    // — grupo de outra categoria não faz sentido, e deixar em tela convida ao erro.
    encherGrupos(item?.grupo_id ?? null);
    encherSubgrupos(item?.grupo_id ?? null, item?.subgrupo_id ?? null);
    cat.addEventListener('change', () => {
      encherGrupos(null);
      preverCodigo();
    });
    gru.addEventListener('change', () => encherSubgrupos(Number(gru.value) || null, null));

    /** Mostra o prefixo que o código novo vai ter, conforme a categoria. */
    function preverCodigo() {
      const previa = form.querySelector<HTMLElement>('#codigo-previa');
      if (!previa) return;
      const c = t.categorias.find((x) => x.id === Number(cat.value));
      previa.textContent = c ? `${c.prefixo}-…  (número gerado ao salvar)` : 'gerado ao salvar';
    }
    preverCodigo();

    // --- galeria ---
    const galeria = form.querySelector<HTMLElement>('#galeria')!;
    function desenharGaleria() {
      galeria.innerHTML =
        fotos.length === 0
          ? `<p class="dica">Nenhuma foto ainda.</p>`
          : fotos
              .map(
                (f, i) => `
          <figure class="gal-item">
            <img src="${escapar(api.foto(f.url) ?? '')}" alt="" loading="lazy">
            ${i === 0 ? '<figcaption class="gal-capa">capa</figcaption>' : ''}
            <div class="gal-acoes">
              <button type="button" data-mover="${i}" data-dir="-1" ${i === 0 ? 'disabled' : ''} title="Mover para trás">◀</button>
              <button type="button" data-mover="${i}" data-dir="1" ${i === fotos.length - 1 ? 'disabled' : ''} title="Mover para frente">▶</button>
              <button type="button" data-tirar="${i}" title="Remover">✕</button>
            </div>
          </figure>`,
              )
              .join('');
    }
    desenharGaleria();

    galeria.addEventListener('click', (e) => {
      const alvo = e.target as HTMLElement;
      const mover = alvo.closest<HTMLElement>('[data-mover]');
      if (mover) {
        const i = Number(mover.dataset['mover']);
        const j = i + Number(mover.dataset['dir']);
        if (j < 0 || j >= fotos.length) return;
        [fotos[i], fotos[j]] = [fotos[j]!, fotos[i]!];
        desenharGaleria();
        return;
      }
      const tirar = alvo.closest<HTMLElement>('[data-tirar]');
      if (tirar) {
        fotos.splice(Number(tirar.dataset['tirar']), 1);
        desenharGaleria();
      }
    });

    const arquivo = form.querySelector<HTMLInputElement>('#arquivo')!;
    const subindo = form.querySelector<HTMLElement>('#subindo')!;
    arquivo.addEventListener('change', async () => {
      const escolhidos = Array.from(arquivo.files ?? []);
      arquivo.value = '';
      for (const [n, f] of escolhidos.entries()) {
        subindo.textContent = `Subindo ${n + 1} de ${escolhidos.length}…`;
        try {
          // Item novo ainda não tem código — a foto sobe sem referência e é
          // religada ao salvar, quando o código nasce.
          const codigo = form.querySelector<HTMLInputElement>('[name="codigo"]')?.value.trim();
          const r = await api.enviarFoto('itens', f, codigo || undefined);
          fotos.push({ caminho: r.caminho, url: r.url });
          desenharGaleria();
        } catch (e) {
          mostrarErro(erro, e instanceof ErroApi ? e.message : `Não consegui subir "${f.name}".`);
          break;
        }
      }
      subindo.textContent = '';
    });

    // --- busca de imagens no Google (Serper) ---
    //
    // A imagem escolhida é BAIXADA para o nosso bucket na hora, nunca linkada
    // do site alheio — a migração das fotos existiu para desfazer exatamente
    // esse tipo de link, que morre quando o site do fornecedor sai do ar.
    const sugestoes = form.querySelector<HTMLElement>('#sugestoes-imagens')!;
    form.querySelector('#buscar-imagens')!.addEventListener('click', async () => {
      const nome = form.querySelector<HTMLInputElement>('[name="nome"]')!.value.trim();
      if (nome.length < 2) {
        mostrarErro(erro, 'Preencha o nome do item primeiro — é ele que vai para a busca.');
        return;
      }
      sugestoes.innerHTML = `<p class="carregando">Buscando imagens para “${escapar(nome)}”…</p>`;
      try {
        const r = await api.get<{ imagens: { imagem: string; miniatura: string; titulo: string }[] }>(
          `/api/itens/imagens?busca=${encodeURIComponent(nome)}`,
        );
        if (r.imagens.length === 0) {
          sugestoes.innerHTML = `<p class="dica">Nenhuma imagem encontrada para esse nome.</p>`;
          return;
        }
        sugestoes.innerHTML = `
          <p class="dica">Clique numa imagem para trazê-la para a galeria. Ela é copiada para o nosso armazenamento — não depende do site de origem continuar no ar.</p>
          <div class="sug-imagens">
            ${r.imagens
              .map(
                (im, n) => `
              <button type="button" class="sug-img" data-importar="${n}" title="${escapar(im.titulo)}">
                <img src="${escapar(im.miniatura)}" alt="" loading="lazy"
                     onerror="this.closest('button').remove()">
              </button>`,
              )
              .join('')}
          </div>`;
        sugestoes.querySelectorAll<HTMLElement>('[data-importar]').forEach((b) => {
          b.addEventListener('click', async () => {
            const im = r.imagens[Number(b.dataset['importar'])]!;
            b.classList.add('sug-baixando');
            try {
              const codigo = form.querySelector<HTMLInputElement>('[name="codigo"]')?.value.trim();
              const imp = await api.post<{ caminho: string; url: string }>('/api/fotos/importar', {
                url: im.imagem,
                pasta: 'itens',
                ...(codigo ? { referencia: codigo } : {}),
              });
              fotos.push({ caminho: imp.caminho, url: imp.url });
              desenharGaleria();
              b.remove();
            } catch (e2) {
              b.classList.remove('sug-baixando');
              mostrarErro(
                erro,
                e2 instanceof ErroApi ? e2.message : 'Não consegui trazer essa imagem — tente outra.',
              );
            }
          });
        });
      } catch (e) {
        sugestoes.innerHTML = '';
        mostrarErro(erro, e instanceof ErroApi ? e.message : 'A busca de imagens falhou.');
      }
    });

    // --- ações ---
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void salvar(form, fotos, erro, !item);
    });

    form.querySelector('#ativar')?.addEventListener('click', () => void alternarAtivo(item!, erro));
    form.querySelector('#excluir')?.addEventListener('click', () => void excluir(item!, erro));
  }

  async function salvar(form: HTMLFormElement, fotos: Foto[], erro: HTMLElement, novo: boolean) {
    const d = new FormData(form);
    const texto = (n: string) => String(d.get(n) ?? '').trim();
    const num = (n: string) => (texto(n) === '' ? null : Number(texto(n)));
    const id = (n: string) => (texto(n) === '' ? null : Number(texto(n)));

    if (!texto('nome')) {
      mostrarErro(erro, 'O nome é obrigatório.');
      return;
    }

    const corpo = {
      nome: texto('nome'),
      categoriaId: id('categoriaId'),
      grupoId: id('grupoId'),
      subgrupoId: id('subgrupoId'),
      unidade: texto('unidade') || null,
      valorUnitario: num('valorUnitario'),
      tipoControle: texto('tipoControle') || null,
      temValidade: d.get('temValidade') === 'on',
      controlaUnidades: d.get('controlaUnidades') === 'on',
      medida1Valor: texto('medida1Valor') || null,
      medida1Unidade: texto('medida1Valor') ? texto('medida1Unidade') || null : null,
      medida2Valor: texto('medida2Valor') || null,
      medida2Unidade: texto('medida2Valor') ? texto('medida2Unidade') || null : null,
      obs: texto('obs') || null,
      sinonimos: texto('sinonimos') || null,
      // A galeria inteira, na ordem final — o que não está aqui, some.
      fotos: fotos.map((f) => f.caminho),
    };

    const botao = form.querySelector<HTMLButtonElement>('#salvar')!;
    botao.disabled = true;
    botao.textContent = 'Salvando…';
    try {
      if (novo) {
        // O código nasce no servidor — prefixo da categoria + sequência.
        const criado = await api.post<{ codigo: string }>('/api/itens', corpo);
        avisar(`Item cadastrado: ${criado.codigo}`);
      } else {
        await api.put(`/api/itens/${encodeURIComponent(texto('codigo'))}`, corpo);
        avisar(`${texto('codigo')} salvo`);
      }
      await carregar();
    } catch (e) {
      botao.disabled = false;
      botao.textContent = novo ? 'Cadastrar' : 'Salvar';
      const msg =
        e instanceof ErroApi
          ? e.message + (e.detalhes?.length ? ` (${e.detalhes.join('; ')})` : '')
          : 'Não consegui salvar.';
      mostrarErro(erro, msg);
    }
  }

  async function alternarAtivo(item: ItemDetalhe, erro: HTMLElement) {
    try {
      await api.patch(`/api/itens/${encodeURIComponent(item.codigo)}/ativo`, { ativo: !item.ativo });
      avisar(item.ativo ? `${item.codigo} desativado` : `${item.codigo} reativado`);
      await carregar();
    } catch (e) {
      mostrarErro(erro, e instanceof ErroApi ? e.message : 'Não consegui alterar.');
    }
  }

  async function excluir(item: ItemDetalhe, erro: HTMLElement) {
    if (!confirm(`Excluir o item ${item.codigo} — ${item.nome}?\n\nIsto não pode ser desfeito.`)) return;
    try {
      await api.del(`/api/itens/${encodeURIComponent(item.codigo)}`);
      avisar(`${item.codigo} excluído`);
      await carregar();
    } catch (e) {
      // O 409 vem com a sugestão certa: item com histórico se desativa, não se
      // apaga. Mostrar a sugestão junto evita a pessoa achar que travou.
      if (e instanceof ErroApi && e.status === 409) {
        mostrarErro(erro, `${e.message} ${e.sugestao ?? 'Use “Desativar”.'}`);
        return;
      }
      mostrarErro(erro, e instanceof ErroApi ? e.message : 'Não consegui excluir.');
    }
  }

  function mostrarErro(el: HTMLElement, msg: string) {
    el.textContent = msg;
    el.hidden = false;
    el.scrollIntoView({ block: 'nearest' });
  }

  void carregar();
}
